import type { TranscodeEffective, TranscodeVideo } from '../api';
import { t } from '../i18n';

/*
 * TRANS-1 (docs/architecture/transcoder.md §9.2): what an output row says about
 * its transcoding. The center sends codes (transcode_effective: state, reasons,
 * reason, hints); the words are made here (M1.5 rule: codes, not parsed text).
 * Owner 22.10: transcoding is off by default; with no transcoder the platform
 * gets the source and the row warns.
 */

/** platforms that have a preset in the built-in catalog (auto resolves) */
export const OPTIMIZABLE: ReadonlySet<string> = new Set(['twitch']);

export interface TranscodeView {
  /** the main line: "Optimized for Twitch · 1080p60 · 6.0 Mbps" */
  label?: string;
  tone: 'live' | 'start' | 'warn' | 'muted';
  /** warning / fact chips, each one sentence */
  notes: { code: string; text: string; tone: 'warn' | 'muted' }[];
  state: TranscodeEffective['state'];
}

const mbps = (kbps?: number) => (kbps ? (kbps / 1000).toFixed(1) : '?');
/** 1920×1080 @ 60 → "1080p60" */
export const resText = (v?: TranscodeVideo) => (v && v.h ? `${v.h}p${v.fps ? Math.round(v.fps) : ''}` : '?');

/** undefined: nothing to show (setting off, destination off the air with nothing decided). */
export function transcodeView(eff: TranscodeEffective | undefined, platform: string): TranscodeView | undefined {
  if (!eff || eff.mode === 'off' || eff.state === 'off') return undefined;
  const p = platform;
  const notes: TranscodeView['notes'] = [];
  const reasons = eff.reasons ?? [];
  const facts = () => {
    if (reasons.includes('bitrate_over')) notes.push({ code: 'bitrate_over', tone: 'muted', text: t('tc.bitrateOver', { in: mbps(eff.input?.kbps), out: mbps(eff.output?.kbps), platform: p }) });
    if (reasons.includes('resolution_over') || reasons.includes('fps_over'))
      notes.push({ code: 'resolution_over', tone: 'muted', text: t('tc.resolutionOver', { in: resText(eff.input), out: resText(eff.output), platform: p }) });
    if (reasons.includes('gop')) notes.push({ code: 'gop', tone: 'muted', text: t('tc.gop', { platform: p }) });
    if (reasons.includes('codec')) notes.push({ code: 'codec', tone: 'muted', text: t('tc.codec', { platform: p }) });
  };
  for (const h of eff.hints ?? []) {
    if (h === 'audio_over') notes.push({ code: 'audio_over', tone: 'warn', text: t('tc.audioOver', { platform: p }) });
  }
  switch (eff.state) {
    case 'running':
      facts();
      return {
        state: eff.state, tone: 'live', notes,
        label: t('tc.running', { platform: p, res: resText(eff.output), mbps: mbps(eff.output?.kbps) }),
      };
    case 'starting':
      facts();
      return { state: eff.state, tone: 'start', notes, label: t('tc.starting', { platform: p }) };
    case 'passthrough':
      return { state: eff.state, tone: 'muted', notes, label: t('tc.passthrough', { platform: p }) };
    case 'fallback_source': {
      const text = eff.input?.kbps && eff.output?.kbps
        ? t('tc.fallbackOver', { platform: p, in: mbps(eff.input.kbps), max: mbps(eff.output.kbps) })
        : t('tc.fallback', { platform: p });
      notes.unshift({ code: 'fallback_source', tone: 'warn', text });
      return { state: eff.state, tone: 'warn', notes, label: t('tc.original', { platform: p }) };
    }
    case 'plan_limit': {
      const key = eff.reason === 'plan_limit_transcode_hours' ? 'tc.planHours' : eff.reason === 'plan_limit_transcode_outputs' ? 'tc.planOutputs' : 'tc.planNone';
      notes.unshift({ code: 'plan_limit', tone: 'warn', text: t(key, { platform: p }) });
      return { state: eff.state, tone: 'warn', notes, label: t('tc.original', { platform: p }) };
    }
  }
  return undefined;
}
